import {saveNativeLogin, requiresNativeCheckpoint} from './native-login-checkpoint';
// Only an authenticated API response may create this checkpoint. Passwords and
// quickSignUpCredentials must never enter persistent browser storage.
const PREFIX = 'OP_SDK_LOGIN_RECOVERY_V1:';
const memory = new Map();
const epochs = new Map();

export function beginLogin(gameId) {
    const id = key(gameId);
    const epoch = (epochs.get(id) || 0) + 1;
    epochs.set(id, epoch);
    return epoch;
}

export function isCurrentLogin(gameId, epoch) { return epochs.get(key(gameId)) === epoch; }

function key(gameId) {
    if (typeof gameId !== 'string' || !gameId || gameId === 'undefined') return null;
    return PREFIX + encodeURIComponent(process.env.endpoint || '') + ':' + encodeURIComponent(gameId);
}

function minimal(session) {
    if (!session || typeof session.accessToken !== 'string' || !session.accessToken.trim()) return null;
    if (typeof session.userId !== 'string' || !session.userId || !key(session.gameId)) return null;
    return {accessToken: session.accessToken, userId: session.userId, gameId: session.gameId};
}

function storage() {
    try { return typeof window !== 'undefined' ? window.localStorage : null; } catch (_) { return null; }
}

export function rememberLogin(session, gameId) {
    const value = minimal({...session, gameId: session && session.gameId || gameId});
    if (!value || (gameId && value.gameId !== gameId)) return false;
    const id = key(value.gameId);
    const record = {version: 1, phase: 'pending', session: value};
    memory.set(id, record);
    try {
        const disk = storage();
        if (!disk) return false;
        disk.setItem(id, JSON.stringify(record));
        return disk.getItem(id) === JSON.stringify(record);
    } catch (_) { return false; }
}

export async function persistAuthenticatedLogin(session, gameId, attempt) {
    if (!isCurrentLogin(gameId, attempt)) return false;
    const webSaved = rememberLogin(session, gameId);
    const value = minimal({...session, gameId: session && session.gameId || gameId});
    if (!value || value.gameId !== gameId) return false;
    const native = await saveNativeLogin(value, () => isCurrentLogin(gameId, attempt));
    if (!isCurrentLogin(gameId, attempt)) return false;
    if (native.required && !native.ok) throw new Error('LOGIN_CHECKPOINT_NOT_ACKNOWLEDGED');
    if (native.ok) {
        const id = key(gameId);
        const record = {...memory.get(id), nativeAcknowledged: true};
        memory.set(id, record);
        try { const disk = storage(); if (disk) disk.setItem(id, JSON.stringify(record)); } catch (_) {}
    }
    return native.ok || webSaved;
}

export function readLogin(gameId) {
    const id = key(gameId);
    if (!id) return null;
    let record = memory.get(id);
    try {
        const raw = storage() && storage().getItem(id);
        if (raw) record = JSON.parse(raw);
    } catch (_) { /* Private mode/storage failure: retain this page's memory. */ }
    const value = record && record.version === 1 && record.phase === 'pending' && minimal(record.session);
    return value && value.gameId === gameId ? value : null;
}

export function clearLogin(gameId, expectedToken) {
    const id = key(gameId);
    if (!id) return;
    const current = readLogin(gameId);
    // An older failed refresh must never erase a newer successful login.
    if (expectedToken && (!current || current.accessToken !== expectedToken)) return;
    beginLogin(gameId);
    memory.delete(id);
    try { const disk = storage(); if (disk) disk.removeItem(id); } catch (_) {}
}

export function applyLoginReset(gameId, nonce) {
    if (!nonce || !key(gameId)) return false;
    // A native logout nonce is consumed once. Reopening after a NEW quick signup
    // must not clear that new account just because the native callback is pending.
    const id = key(gameId) + ':reset';
    try {
        const disk = storage();
        if (nonce !== '1' && disk && disk.getItem(id) === nonce) return false;
        clearLogin(gameId);
        if (disk) disk.setItem(id, nonce);
    } catch (_) { clearLogin(gameId); }
    return true;
}

export function completeLoginHandoff(session) {
    if (!session) return;
    if (requiresNativeCheckpoint()) {
        const id = key(session.gameId);
        let record = memory.get(id);
        try { const raw = storage() && storage().getItem(id); if (raw) record = JSON.parse(raw); } catch (_) {}
        if (!record || !record.nativeAcknowledged || !record.session || record.session.accessToken !== session.accessToken) return;
    }
    // The upgraded SDK already acknowledged durable storage. For older native
    // SDKs retain R47's final-handoff behavior so explicit logout is not undone.
    // This clears only the web checkpoint; it must never log out the native SDK.
    clearLogin(session.gameId, session.accessToken);
}
